import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Search, FileCheck, Shield, TrendingUp } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function DueDiligenceProcess() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Search, title: 'Comprehensive Review', description: 'Thorough investigation of legal, financial, and operational aspects.' },
    { icon: FileCheck, title: 'Risk Identification', description: 'Identify potential liabilities and compliance issues.' },
    { icon: Shield, title: 'Deal Protection', description: 'Protect your interests in M&A, investments, and partnerships.' },
    { icon: TrendingUp, title: 'Informed Decisions', description: 'Make strategic decisions based on complete information.' },
  ];

  const faqs = [
    { question: 'What is due diligence?', answer: 'Due diligence is a comprehensive investigation and evaluation of a business, typically conducted before mergers, acquisitions, investments, or partnerships to assess risks and opportunities.' },
    { question: 'What does due diligence cover?', answer: 'Due diligence covers legal compliance, financial records, contracts, intellectual property, employment matters, litigation history, tax compliance, and operational procedures.' },
    { question: 'How long does due diligence take?', answer: 'The process typically takes 2-8 weeks depending on company size, complexity, and the depth of review required. Expedited reviews are available for time-sensitive transactions.' },
    { question: 'Who needs due diligence?', answer: 'Buyers, investors, lenders, and partners conducting M&A transactions, making investments, or entering significant business relationships benefit from due diligence.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>Due Diligence Process</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Comprehensive due diligence services for transactions and investments.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our Due Diligence Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Protect your investments with thorough due diligence.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about due diligence.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Need Due Diligence Services?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Protect your business interests with comprehensive due diligence.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="due_diligence" />
      <Footer />
    </div>
  );
}